import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DISPLAY_DATE_FORMAT } from 'src/constants/date';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Returns date format depends on diff time with incoming date
 * "now" for times within the last minute.
 * "X min" for times within the last hour (e.g., 3 min, 15 min).
 * "X hr" for times within the last 24 hours (e.g., 1 hr, 5 hr).
 * "1 day" for times within the last 24-48 hours.
 * "2 days" for times within 48-72 hours.
 * "M/D/YY" for times older than 72 hours.
 */
export const formatLastMessageSinceTime = (isoDateString?: string) => {
  if (!isoDateString) {
    return '';
  }

  const now = dayjs();
  const date = dayjs(isoDateString);
  const diffInMinutes = now.diff(date, 'minute');

  if (diffInMinutes < 1) {
    return 'now';
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min`;
  }

  const diffInHours = now.diff(date, 'hour');

  if (diffInHours < 24) {
    return `${diffInHours} hr`;
  }

  const diffInDays = now.diff(date, 'day');

  if (diffInDays === 1) {
    return '1 day';
  }

  if (diffInDays === 2) {
    return '2 days';
  }

  return date.format('M/D/YY');
};

export const isDefault12HourFormat = () => {
  return new Intl.DateTimeFormat(navigator.language, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: undefined,
  }).resolvedOptions().hour12;
};

export function getHumanReadableDate(date?: string, withSuffix = true) {
  if (!date) {
    return '';
  }
  return dayjs(date).fromNow(!withSuffix);
}

export function getFormattedDate(date?: string, timeFormat?: string) {
  if (!date) {
    return '';
  }
  const format = timeFormat ? `ddd, MMMM D [at] ${timeFormat} z` : 'ddd, MMMM D, YYYY';
  return dayjs(date).format(format);
}

export const formatDisplayDate = (date?: string): string =>
  date ? dayjs(date).format(DISPLAY_DATE_FORMAT) : '';

export const toDayjs = (value: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);

  return parsed.isValid() ? parsed : null;
};

export const toIso = (next: Dayjs | null) => (next?.isValid() ? next.toISOString() : null);
